import React, { useState, useEffect } from 'react';
import InternalTransfer from './transfers/InternalTransfer';
import ExternalUSTransfer from './transfers/ExternalUSTransfer';
import InternationalTransfer from './transfers/InternationalTransfer';
import UserTransfer from './transfers/UserTransfer';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const DEFAULT_TRANSFER_LIMIT = 2;

const US_BANKS = [
  { name: 'JPMorgan Chase', routingNumber: '021000021' },
  { name: 'Bank of America', routingNumber: '026009593' },
  { name: 'Wells Fargo', routingNumber: '121000248' },
  { name: 'Citibank', routingNumber: '021000089' },
  { name: 'U.S. Bank', routingNumber: '091000022' },
  { name: 'PNC Bank', routingNumber: '031000053' },
  { name: 'Capital One', routingNumber: '031176110' },
  { name: 'TD Bank', routingNumber: '011103093' },
  { name: 'Truist', routingNumber: '061000104' },
  { name: 'Ally Bank', routingNumber: '124003116' }
];

export default function TransferTabs({ accounts, onTransfer, currentUser }) {
  const { currentUser: authUser } = useAuth();
  const [activeTab, setActiveTab] = useState('internal');
  const [transferCount, setTransferCount] = useState(0);
  const [limitEnabled, setLimitEnabled] = useState(true);
  const [limitAmount, setLimitAmount] = useState(DEFAULT_TRANSFER_LIMIT);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showLimitSpinner, setShowLimitSpinner] = useState(false);

  // UI rule: if an Inheritance account exists, hide Savings everywhere in transfers.
  const hasInheritance = Array.isArray(accounts) && accounts.some((a) => String(a?.type || '').toLowerCase() === 'inheritance');
  const transferAccounts = hasInheritance
    ? (accounts || []).filter((a) => String(a?.type || '').toLowerCase() !== 'savings')
    : (accounts || []);

  // Fetch admin setting and per-user override (real-time)
  useEffect(() => {
    if (!authUser?.uid) return;

    let global = { isEnabled: true, transactionLimit: DEFAULT_TRANSFER_LIMIT };
    let override = null;

    const apply = () => {
      if (override && typeof override === 'object') {
        setLimitEnabled(override.isEnabled !== false);
        setLimitAmount(override.transactionLimit ?? global.transactionLimit ?? DEFAULT_TRANSFER_LIMIT);
      } else {
        setLimitEnabled(global.isEnabled !== false);
        setLimitAmount(global.transactionLimit ?? DEFAULT_TRANSFER_LIMIT);
      }
    };

    const unsubGlobal = onSnapshot(
      doc(db, 'adminSettings', 'transferLimits'),
      (snap) => {
        const s = snap.exists() ? snap.data() : {};
        global = {
          isEnabled: s.isEnabled !== false,
          transactionLimit: s.transactionLimit ?? DEFAULT_TRANSFER_LIMIT,
        };
        apply();
      },
      () => {
        global = { isEnabled: true, transactionLimit: DEFAULT_TRANSFER_LIMIT };
        apply();
      }
    );

    const unsubUser = onSnapshot(
      doc(db, 'users', authUser.uid),
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        override = d.transferLimit || null;
        apply();
      },
      () => {
        override = null;
        apply();
      }
    );

    // Apply once immediately so UI isn't blank between snapshots
    apply();

    return () => {
      unsubGlobal();
      unsubUser();
    };
  }, [authUser?.uid]);

  useEffect(() => {
    const fetchTransferCount = async () => {
      if (authUser) {
        const userDoc = await getDoc(doc(db, 'users', authUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const count = userData.transferCount || userData.transfers || userData.transfer_count || 0;
          setTransferCount(count);
        }
      }
    };
    fetchTransferCount();
  }, [authUser]);

  const handleTransfer = async (data) => {
    if (limitEnabled && transferCount >= limitAmount) {
      setShowLimitSpinner(true);
      window.setTimeout(() => {
        setShowLimitSpinner(false);
        setShowLimitModal(true);
      }, 650);
      return;
    }

    if (limitEnabled) {
      const newCount = transferCount + 1;
      setTransferCount(newCount);
      await updateDoc(doc(db, 'users', authUser.uid), {
        transferCount: newCount
      });
    }

    return onTransfer({
      ...data,
      userId: currentUser.uid
    });
  };

  const tabs = [
    { 
      id: 'internal', 
      label: 'Between Accounts', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )
    },
    { 
      id: 'user', 
      label: 'To Claireoaks member', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    { 
      id: 'external', 
      label: 'To US Bank', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    { 
      id: 'international', 
      label: 'International', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Section Title */}
      <div className="text-center">
        <h2 className="text-2xl font-playfair font-bold text-gray-900">Transfer Money</h2>
        <p className="mt-2 text-sm text-gray-600">Choose a transfer type to get started</p>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative group flex flex-col items-center justify-center p-4 sm:p-6
                transition-all duration-200 hover:bg-gray-50
                ${activeTab === tab.id ? 'bg-primary-50' : 'bg-white'}
              `}
            >
              {/* Active Indicator */}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-600" />
              )}
              
              {/* Icon */}
              <div className={`
                p-3 rounded-xl mb-2
                ${activeTab === tab.id 
                  ? 'text-primary-600 bg-primary-100' 
                  : 'text-gray-500 bg-gray-50 group-hover:bg-gray-100 group-hover:text-gray-600'}
                transition-colors duration-200
              `}>
                {tab.icon}
              </div>
              
              {/* Label */}
              <span className={`
                text-sm font-medium
                ${activeTab === tab.id ? 'text-primary-900' : 'text-gray-600 group-hover:text-gray-900'}
              `}>
                {tab.label}
              </span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6 sm:p-8 animate-fadeIn">
          {activeTab === 'internal' && (
            <InternalTransfer
              accounts={transferAccounts}
              onTransfer={(data) => handleTransfer({ ...data, type: 'internal' })}
            />
          )}
          {activeTab === 'user' && (
            <UserTransfer
              accounts={transferAccounts}
              currentUser={currentUser}
              onTransfer={(data) => handleTransfer({ ...data, type: 'user' })}
            />
          )}
          {activeTab === 'external' && (
            <ExternalUSTransfer
              accounts={transferAccounts}
              banks={US_BANKS}
              onTransfer={(data) => handleTransfer({ ...data, type: 'external' })}
            />
          )}
          {activeTab === 'international' && (
            <InternationalTransfer
              accounts={transferAccounts}
              onTransfer={(data) => handleTransfer({ ...data, type: 'international' })}
            />
          )}
        </div>
      </div>

      {/* Transfer Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full mx-auto p-6 space-y-4 relative animate-fadeIn">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-100 rounded-full p-3">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-8 w-8 text-red-600" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-center text-gray-900">
              Transaction Blocked
            </h3>
            
            <p className="text-gray-600 text-center">
              For security reasons, this transaction has been blocked. Please contact our support team for assistance.
            </p>

            <div className="mt-4 space-y-3">
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-900 mb-1">Contact Support</p>
                <p>📧 support@claireoakscu.com</p>
                <p>🕒 Monday - Friday: 8AM - 8PM ET</p>
              </div>
              
              <button
                onClick={() => setShowLimitModal(false)}
                className="w-full py-3 px-4 border border-transparent rounded-xl shadow-sm text-white 
                  bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 
                  transform transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Limit check spinner */}
      {showLimitSpinner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full border-2 border-emerald-600/30 border-t-emerald-700 animate-spin" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Checking transfer limit…</p>
                <p className="mt-1 text-sm text-gray-600">Please wait.</p>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}